import { Icon, type IconName } from './Icon';
import { ICON_SIZE } from '../styles/tokens';
import './SoundButton.css';

type Props = {
  icon: IconName;
  label: string;
  color?: 'brand' | 'accent' | 'subtle';
  /** Small counter badge — shown when > 1 (ej. una palabra con varios efectos). */
  badgeCount?: number;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  title?: string;
  className?: string;
};

/** Color semántico → variante de `.btn` (buttons.css). `subtle` es el blanco de base. */
const COLOR_CLASS: Record<NonNullable<Props['color']>, string> = {
  brand: 'btn--primary',
  accent: 'btn--cyan',
  subtle: '',
};

/**
 * The one "play a sound" button shape used across the app — every card
 * action (Sonido/efecto, Nombre, Sílaba…) is this same labeled pill, always
 * in the fixed action row under a card's content. Three semantic colors:
 * brand = lectura primaria, subtle = lectura secundaria, accent = efecto
 * crudo (no pasa por useSay, no resalta nada — ver DetailView's CardActions).
 */
export function SoundButton({
  icon,
  label,
  color = 'brand',
  badgeCount,
  onClick,
  ariaLabel,
  title,
  className,
}: Props) {
  const classes = [
    'btn sound-button',
    COLOR_CLASS[color],
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      title={title ?? ariaLabel ?? label}
    >
      <Icon name={icon} size={ICON_SIZE.md} />
      <span>{label}</span>
      {badgeCount != null && badgeCount > 1 && (
        <span className="sound-button-badge" aria-hidden="true">{badgeCount}</span>
      )}
    </button>
  );
}
